import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, ChevronDown, ChevronRight, Plus, Trash2, X, Check } from 'lucide-react';
import { authFetch } from "../../api";
import CustomMeasurables from './CustomMeasurables';
import GradeHistory from './GradeHistory';

const API = 'https://hs.aida-advisory.com';

// ── Tooltip component ────────────────────────────────────────────────────────
const Tooltip = ({ text, children }) => {
  const [visible, setVisible] = useState(false);
  if (!text) return children;
  return (
    <span className="relative inline-block"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <span className="absolute z-50 left-0 top-6 bg-[#0A1628] border border-[#00D4FF]/40 text-[#B0BEC5] text-xs rounded-lg px-3 py-2 w-48 md:w-56 max-w-[75vw] shadow-xl pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
};

// ── Grade color helper ───────────────────────────────────────────────────────
const gradeColor = (g) => {
  if (g === '' || g === null || g === undefined) return '#546E7A';
  if (g >= 8) return '#00E676';
  if (g >= 7) return '#00C853';
  if (g >= 6) return '#FFD600';
  if (g >= 4) return '#FFB300';
  return '#EF5350';
};

// ── Inline editable text ─────────────────────────────────────────────────────
const InlineEdit = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const inputRef = useRef();

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    if (val.trim()) onSave(val.trim());
    else setVal(value);
  };

  if (!editing) return (
    <span className="cursor-pointer hover:text-[#00D4FF] transition-colors" onClick={() => setEditing(true)}>
      {value}
    </span>
  );

  return (
    <span className="flex items-center gap-1">
      <input ref={inputRef} value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(value); } }}
        className="bg-[#0A1628] text-white border border-[#00D4FF] rounded px-2 py-0.5 text-sm focus:outline-none w-40" />
      <button onClick={save} className="text-[#00E676]"><Check size={14} /></button>
      <button onClick={() => { setEditing(false); setVal(value); }} className="text-[#EF5350]"><X size={14} /></button>
    </span>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const GradingTab = ({ playerId, playerName, playerPosition }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [categories, setCategories] = useState([]);
  const [grades, setGrades] = useState({});
  const [notes, setNotes] = useState({});
  const [scoutName, setScoutName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [expanded, setExpanded] = useState({});
  const saveTimer = useRef({});
  const noteTimer = useRef({});
  const weightTimer = useRef({});
  const scoutNameRef = useRef(scoutName);

  useEffect(() => { scoutNameRef.current = scoutName; }, [scoutName]);

  // ── Fetch everything fresh ─────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [hierRes, gradesRes, notesRes] = await Promise.all([
        authFetch(`${API}/api/criteria/hierarchy?position=${playerPosition}`),
        authFetch(`${API}/api/grades/player/${playerId}`),
        authFetch(`${API}/api/criteria/category-notes/${playerId}`)
      ]);

      if (hierRes.ok) {
        const hier = await hierRes.json();
        setCategories(hier);
        if (hier.length > 0) {
          setExpanded({});
        }
      }

      if (gradesRes.ok) {
        const gradesData = await gradesRes.json();
        const gradesObj = {};
        gradesData.forEach(g => {
          gradesObj[g.criterion_id] = g.grade;
          if (g.scout_name) setScoutName(prev => prev || g.scout_name);
        });
        setGrades(gradesObj);
      }

      if (notesRes.ok) {
        const notesData = await notesRes.json();
        const notesObj = {};
        notesData.forEach(n => { notesObj[n.category_id] = n.notes; });
        setNotes(notesObj);
      }
    } catch (err) {
      console.error('Error fetching grading data:', err);
    } finally {
      setLoading(false);
    }
  }, [playerId, playerPosition]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Auto-save grade ────────────────────────────────────────────────────────
  const handleGradeChange = (subId, value) => {
    if (value === '') {
      setGrades(prev => ({ ...prev, [subId]: '' }));
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > 9) return;
    setGrades(prev => ({ ...prev, [subId]: num }));

    clearTimeout(saveTimer.current[subId]);
    saveTimer.current[subId] = setTimeout(async () => {
      setSaveStatus('Saving...');
      try {
        await authFetch(`${API}/api/grades/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            player_id: playerId,
            grades: [{ criterion_id: subId, grade: num, notes: '' }],
            scout_name: scoutNameRef.current,
            recalculate_fit: true
          })
        });
        setSaveStatus('Saved ✓');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch {
        setSaveStatus('Save failed');
      }
    }, 1500);
  };

  // ── Auto-save category note ────────────────────────────────────────────────
  const handleNoteChange = (catId, value) => {
    setNotes(prev => ({ ...prev, [catId]: value }));
    clearTimeout(noteTimer.current[catId]);
    noteTimer.current[catId] = setTimeout(async () => {
      setSaveStatus('Saving...');
      try {
        await authFetch(`${API}/api/evaluator-notes/${playerId}`, {
          method: 'POST',
          body: JSON.stringify({ category_id: catId, note_text: value })
        }).catch(() => {});
        await authFetch(`${API}/api/criteria/category-notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            player_id: playerId,
            category_id: catId,
            notes: value,
            scout_name: scoutNameRef.current
          })
        });
        setSaveStatus('Saved ✓');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch {
        setSaveStatus('Save failed');
      }
    }, 1500);
  };

  // ── Calculate category grade from sub-criteria ─────────────────────────────
  const calcCategoryGrade = (subCriteria) => {
    const graded = subCriteria.filter(sc => grades[sc.id] !== '' && grades[sc.id] !== undefined && grades[sc.id] !== null);
    if (graded.length === 0) return null;
    const totalWeight = graded.reduce((sum, sc) => sum + sc.weight, 0);
    const weighted = graded.reduce((sum, sc) => sum + (grades[sc.id] * sc.weight), 0);
    return totalWeight > 0 ? (weighted / totalWeight).toFixed(1) : null;
  };

  // ── Overall progress ───────────────────────────────────────────────────────
  const totalSubs = categories.reduce((sum, cat) => sum + cat.sub_criteria.length, 0);
  const gradedSubs = categories.reduce((sum, cat) =>
    sum + cat.sub_criteria.filter(sc => grades[sc.id] !== '' && grades[sc.id] !== undefined && grades[sc.id] !== null).length, 0);
  const progressPct = totalSubs > 0 ? Math.round((gradedSubs / totalSubs) * 100) : 0;

  // ── Edit: rename criterion ─────────────────────────────────────────────────
  const renameCriterion = async (id, name) => {
    await authFetch(`${API}/api/criteria/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    fetchAll();
  };

  // ── Edit: delete criterion ─────────────────────────────────────────────────
  const deleteCriterion = async (id) => {
    if (!window.confirm('Delete this criterion?')) return;
    await authFetch(`${API}/api/criteria/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  // ── Edit: add sub-criterion ────────────────────────────────────────────────
  // ── Edit: update weight ────────────────────────────────────────────────────
  const updateWeight = (id, pct) => {
    const num = parseFloat(pct);
    if (isNaN(num) || num < 0 || num > 100) return;
    const decimal = parseFloat((num / 100).toFixed(4));
    // Optimistically update local categories
    setCategories(prev => prev.map(cat => {
      if (cat.id === id) return { ...cat, weight: decimal };
      return {
        ...cat,
        sub_criteria: cat.sub_criteria.map(sc =>
          sc.id === id ? { ...sc, weight: decimal } : sc
        )
      };
    }));
    clearTimeout(weightTimer.current[id]);
    weightTimer.current[id] = setTimeout(async () => {
      setSaveStatus('Saving...');
      try {
        await authFetch(`${API}/api/criteria/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weight: decimal })
        });
        setSaveStatus('Saved ✓');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch {
        setSaveStatus('Save failed');
      }
    }, 1000);
  };

  if (loading) return (
    <div className="text-center py-12">
      <div className="text-[#00D4FF] text-lg">Loading grading template...</div>
    </div>
  );

  if (categories.length === 0) return (
    <div className="panel-card text-center py-12">
      <div className="text-white text-lg mb-2">No Criteria Found</div>
      <div className="text-[#B0BEC5] text-sm">No criteria exist for the {playerPosition} position yet.</div>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-white text-xl md:text-2xl font-bold truncate">{playerName}</h2>
          <div className="text-[#00D4FF] text-sm font-semibold">{playerPosition}</div>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus && (
            <span className={`text-sm transition-opacity ${saveStatus === 'Saved ✓' ? 'text-[#00E676]' : 'text-[#FFB300]'}`}>
              {saveStatus}
            </span>
          )}

        </div>
      </div>

      {/* Scout name */}
      <div className="panel-card py-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="text-[#B0BEC5] text-sm whitespace-nowrap">Evaluator Name:</label>
          <input
            type="text"
            value={scoutName}
            onChange={e => setScoutName(e.target.value)}
            placeholder="Enter your name"
            className="flex-1 bg-[#243B55] text-white px-3 py-2 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none transition-colors text-sm"
          />
        </div>
      </div>

      {/* Progress */}
      <div className="panel-card py-3">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[#B0BEC5] text-sm">Overall Progress</span>
          <span className="text-[#00D4FF] font-bold text-sm">{gradedSubs} / {totalSubs} graded ({progressPct}%)</span>
        </div>
        <div className="w-full h-2 bg-[#243B55] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#00D4FF] to-[#00E676] transition-all duration-500"
            style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Categories */}
      {(() => {
        const catWeightTotal = Math.round(categories.reduce((sum, cat) => sum + (cat.weight || 0), 0) * 100);
        const catWeightOk = catWeightTotal === 100;
        return (
          <>
            {categories.map(cat => {
        const catGrade = calcCategoryGrade(cat.sub_criteria);
        const catGraded = cat.sub_criteria.filter(sc =>
          grades[sc.id] !== '' && grades[sc.id] !== undefined && grades[sc.id] !== null).length;
        const isOpen = !!expanded[cat.id];
        const isComplete = cat.sub_criteria.length > 0 && catGraded === cat.sub_criteria.length;

        return (
          <div key={cat.id} className="panel-card p-0 overflow-hidden border border-[#243B55] rounded-xl">

            {/* Category header - click to expand/collapse */}
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#243B55]/40 transition-colors select-none"
              onClick={() => setExpanded(prev => ({ ...prev, [cat.id]: !prev[cat.id] }))}>

              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                {isOpen
                  ? <ChevronDown size={16} className="text-[#00D4FF] flex-shrink-0" />
                  : <ChevronRight size={16} className="text-[#546E7A] flex-shrink-0" />}

                <span className="text-white font-semibold truncate">{cat.name}</span>

                <span className="text-[#546E7A] text-xs flex-shrink-0">
                  {catGraded} / {cat.sub_criteria.length}
                </span>

              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                {catGrade !== null && (
                  <span className="font-bold text-xl font-rajdhani" style={{ color: gradeColor(parseFloat(catGrade)) }}>
                    {catGrade}
                  </span>
                )}
                {isComplete && <CheckCircle size={15} className="text-[#00E676]" />}

              </div>
            </div>

            {/* Expanded content */}
            {isOpen && (
              <div className="border-t border-[#243B55]">

                {/* Sub-criteria list */}
                <div className="px-4 pt-2 pb-1">
                  {cat.sub_criteria.length === 0 && (
                    <div className="text-[#546E7A] text-sm py-2 italic">No sub-criteria yet. Add one below.</div>
                  )}
                  {cat.sub_criteria.map(sc => {
                    const g = grades[sc.id];
                    const isGraded = g !== '' && g !== undefined && g !== null;
                    return (
                      <div key={sc.id}
                        className="flex items-center gap-3 py-2 border-b border-[#243B55]/50 last:border-0">

                        {/* Check + name */}
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          {isGraded
                            ? <CheckCircle size={13} className="text-[#00E676] flex-shrink-0" />
                            : <span className="w-[13px] flex-shrink-0" />}

                          <Tooltip text={sc.description || null}>
                              <span className="text-[#B0BEC5] text-sm cursor-default">{sc.name}</span>
                            </Tooltip>
                        </div>


                        {/* Grade input */}
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0" max="9" step="0.1"
                          value={isGraded ? g : ''}
                          onChange={e => handleGradeChange(sc.id, e.target.value)}
                          placeholder="—"
                          className="w-16 min-h-[40px] bg-[#243B55] text-center rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none py-1.5 text-sm font-bold font-rajdhani transition-colors flex-shrink-0"
                          style={{ color: isGraded ? gradeColor(g) : '#546E7A' }}
                        />
                      </div>
                    );
                  })}

                </div>

                {/* Category notes */}
                <div className="px-4 pb-3 pt-1">
                  <textarea
                    value={notes[cat.id] || ''}
                    onChange={e => handleNoteChange(cat.id, e.target.value)}
                    placeholder={`Scout notes on ${cat.name}...`}
                    rows={2}
                    className="w-full bg-[#0A1628] text-[#B0BEC5] placeholder-[#546E7A] px-3 py-2 rounded-lg border border-[#546E7A]/20 focus:border-[#00D4FF]/50 focus:outline-none transition-colors text-sm resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
          </>
        );
      })()}

      {/* Grade History Button */}
      <div className="mt-6 flex justify-end">
        <button onClick={() => setShowHistory(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#243B55] text-[#00D4FF] rounded-lg text-sm font-medium hover:bg-[#2D4A6A] transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          Evaluation History
        </button>
      </div>

      {showHistory && <GradeHistory playerId={playerId} onClose={() => setShowHistory(false)} />}

      {/* Custom Measurables Section */}
      <CustomMeasurables playerId={playerId} playerPosition={playerPosition} />

    </div>
  );
};

export default GradingTab;
